import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CandidateDetailDrawer from '../components/Summary/CandidateDetailDrawer';
import { TableRow } from '../components/command-center/CandidateTable';
import CandidateListPanel from '../components/command-center/CandidateListPanel';
import ToolbarButton from '../components/command-center/ToolbarButton';
import ErrorAlert from '../components/Common/ErrorAlert';
import apiService from '../services/api';
import { useDirection } from '../context/DirectionContext';
import { translate as tr } from '../i18n/translations';
import { T, Tier } from '../theme/tokens';
import {
  CandidateDetailRecord,
  CandidateStatusDto,
  CycleFormAssignment,
  CycleStatus,
  FormDefinition,
  FormSubmissionRecord,
  TeamAssignment,
  UserRole,
} from '../types/api';
import { buildExportCSV, downloadCSV, findEnrichedValue } from '../utils/csvExport';
import { buildCandidateImageMap } from '../utils/candidateImages';

// ── Helpers ───────────────────────────────────────────────────────────────────

const VALID_TIERS = new Set(['top', 'mid', 'bottom']);

export function extractPositionInfo(
  submissions: FormSubmissionRecord[],
  teamleadId?: string
): { position: number; teamSize: number; section?: Tier; evaluatorName: string } | null {
  const fromSub = (sub: FormSubmissionRecord): { position: number; teamSize: number; section?: Tier } | null => {
    const data = sub.submissionData;
    const parseSection = (v: unknown): Tier | undefined =>
      typeof v === 'string' && VALID_TIERS.has(v) ? (v as Tier) : undefined;

    if (data.position != null && data['team-size'] != null) {
      const pos = Number(data.position);
      const ts  = Number(data['team-size']);
      if (!isNaN(pos) && !isNaN(ts) && pos > 0 && ts > 0)
        return { position: pos, teamSize: ts, section: parseSection(data.section) };
    }
    const raw = data.comments;
    if (typeof raw === 'string' && raw.trim().startsWith('{')) {
      try {
        const p = JSON.parse(raw) as Record<string, unknown>;
        const pos = Number(p.position);
        const ts  = Number(p['team-size']);
        if (!isNaN(pos) && !isNaN(ts) && pos > 0 && ts > 0)
          return { position: pos, teamSize: ts, section: parseSection(p.section) };
      } catch { /* ignore */ }
    }
    return null;
  };

  const byFirstName = (a: FormSubmissionRecord, b: FormSubmissionRecord) =>
    (a.submittedBy.firstName ?? '').localeCompare(b.submittedBy.firstName ?? '', undefined, { sensitivity: 'base' });

  // Teamlead submission first (if known), then everyone else sorted A→Z
  const teamleadFirst = (subs: FormSubmissionRecord[]): FormSubmissionRecord[] => {
    if (!teamleadId) return [...subs].sort(byFirstName);
    const tl   = subs.filter(s =>  s.submittedBy.id === teamleadId);
    const rest = subs.filter(s =>  s.submittedBy.id !== teamleadId).sort(byFirstName);
    return [...tl, ...rest];
  };

  // 'teamleadRank' is the dedicated form type for this data (see
  // TeamleadRankScoreStrategy on the backend — position/team-size are only
  // ever submitted under it), so it's checked first. 'generic'/'interview'
  // stay as a fallback for any submission that happens to carry the same
  // shape under a different type, but they were never the intended source.
  for (const formType of ['teamleadRank', 'generic', 'interview'] as const) {
    for (const sub of teamleadFirst(submissions.filter(s => s.formType === formType))) {
      const info = fromSub(sub);
      if (info) return { ...info, evaluatorName: `${sub.submittedBy.firstName} ${sub.submittedBy.lastName}`.trim() };
    }
  }
  return null;
}

export function positionToTier(position: number, teamSize: number): Tier {
  if (position <= Math.ceil(teamSize / 3))     return 'top';
  if (position <= Math.ceil(2 * teamSize / 3)) return 'mid';
  return 'bottom';
}


function computeRedFlagSet(
  details: CandidateDetailRecord[],
  formDefMap: Map<string, FormDefinition>
): Set<string> {
  const flagged = new Set<string>();
  for (const detail of details) {
    outer:
    for (const sub of detail.formSubmissions) {
      const fd = formDefMap.get(sub.formDefinitionId);
      const fields: Array<{ type: string; fieldKey: string }> = fd?.structure?.structure ?? [];
      const flagKeys = fields.filter(f => f.type === 'flagblock').map(f => f.fieldKey);
      if (flagKeys.length === 0) continue;

      const raw = sub.submissionData.comments;
      let answers: Record<string, unknown> = {};
      if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try { answers = JSON.parse(raw) as Record<string, unknown>; } catch { /* ignore */ }
      } else if (raw && typeof raw === 'object') {
        answers = raw as Record<string, unknown>;
      }

      for (const key of flagKeys) {
        const val = answers[key];
        if (val === 1 || val === '1' || val === true || val === 'true') {
          flagged.add(detail.id);
          break outer;
        }
      }
    }
  }
  return flagged;
}

function parseSimpleCSV(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseRow = (line: string): string[] => {
    const vals: string[] = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    vals.push(cur.trim()); return vals;
  };
  const headers = parseRow(lines[0]);
  return { headers, rows: lines.slice(1).map(l => Object.fromEntries(headers.map((h, i) => [h, parseRow(l)[i] ?? '']))) };
}

// ── Main page ─────────────────────────────────────────────────────────────────

const SummaryPage: React.FC = () => {
  const { lang } = useDirection();

  // ── Cycle & filter state ──
  const [cycleId, setCycleId] = useState('');
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter]     = useState('all');
  const [matamFilter, setMatamFilter]   = useState('all');
  const [tierFilter, setTierFilter]     = useState<Tier | 'all'>('all');
  const [evalFilter, setEvalFilter]     = useState('all');

  // ── UI state ──
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateStatusDto | null>(null);
  const [exportAnchor, setExportAnchor]           = useState<null | HTMLElement>(null);
  const [exporting, setExporting]                 = useState(false);
  const [toast, setToast]                         = useState<{ text: string; sev: 'success' | 'error' | 'warning' } | null>(null);
  const [isFullscreen, setIsFullscreen]           = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed]   = useState(false);
  // Zoom is a per-session convenience for the fullscreen detail view only —
  // always resets to 100% when fullscreen is exited, never persisted.
  const [textZoom, setTextZoom]                   = useState(1);
  const exitFullscreen = useCallback(() => { setIsFullscreen(false); setTextZoom(1); }, []);

  // ── CSV enrichment ──
  const [csvEnrichment, setCsvEnrichment] = useState(new Map<number, Record<string, string>>());
  const [hasUnsavedCsv, setHasUnsavedCsv] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // ── Candidate images (session-only, client-side — mirrors CSV enrichment) ──
  const [imageMap, setImageMap] = useState(new Map<number, File>());
  const imagesInputRef = useRef<HTMLInputElement>(null);

  // ── ⌘K focus search ──
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') exitFullscreen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [exitFullscreen]);

  // ── Queries ──
  const { data: cycles }   = useQuery({ queryKey: ['cycles'], queryFn: () => apiService.getCycles() });
  const { data: cycleStatus, isLoading: cycleLoading, error: cycleError } = useQuery({
    queryKey: ['cycleStatus', cycleId],
    queryFn: () => apiService.getCycleStatus(cycleId),
    enabled: !!cycleId,
  });
  const { data: formAssignments = [] } = useQuery<CycleFormAssignment[]>({
    queryKey: ['formAssignments', cycleId],
    queryFn: () => apiService.getFormAssignmentsByCycle(cycleId),
    enabled: !!cycleId,
  });
  const { data: teamAssignments = [] } = useQuery<TeamAssignment[]>({
    queryKey: ['teamAssignments', cycleId],
    queryFn: () => apiService.getTeamAssignments(cycleId),
    enabled: !!cycleId,
  });

  const formDefs = useMemo<FormDefinition[]>(() =>
    formAssignments.map(a => ({
      id: a.id,
      name: a.template.name,
      formType: a.formType as FormDefinition['formType'],
      roleRelevancy: a.template.roleRelevancy ?? [],
      isActive: a.isActive,
      structure: a.structureSnapshot,
      weightInFinalScore: Number(a.weightInFinalScore),
      isMandatoryForClosure: a.isMandatoryForClosure,
      isTemplate: false,
      cycle: null,
      createdAt: a.createdAt,
      updatedAt: a.updatedAt,
    })),
    [formAssignments]
  );

  const candidates: CandidateStatusDto[] = useMemo(
    () => (cycleStatus?.teams ?? []).flatMap(t => t.candidates ?? []),
    [cycleStatus]
  );

  const { data: allDetails, isLoading: namesLoading } = useQuery({
    queryKey: ['allCandidateDetails', cycleId, candidates.length],
    queryFn: () => apiService.getCandidateDetailsBatch(candidates.map(c => c.id)),
    enabled: candidates.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // ── Computed ──
  const sortedCycles = useMemo(
    () => [...(cycles ?? [])].sort((a, b) => ({ active: 0, draft: 1, closed: 2 } as any)[a.status] - ({ active: 0, draft: 1, closed: 2 } as any)[b.status]),
    [cycles]
  );

  const cycleOptions = useMemo(
    () => sortedCycles.map(c => ({
      value: c.id,
      label: `${c.displayName}${c.status === CycleStatus.ACTIVE ? ' ●' : ''}`,
    })),
    [sortedCycles]
  );

  const nameMap = useMemo(() => {
    const m = new Map<string, CandidateDetailRecord>();
    if (allDetails) for (const d of allDetails.candidates) m.set(d.id, d);
    return m;
  }, [allDetails]);

  const teamleadIdByTeam = useMemo(() => {
    const map = new Map<number, string>();
    for (const ta of teamAssignments) {
      if (ta.role === UserRole.TEAMLEAD) map.set(ta.teamNumber, ta.user.id);
    }
    return map;
  }, [teamAssignments]);

  const positionMap = useMemo(() => {
    const map = new Map<string, { position: number; teamSize: number; section?: Tier; evaluatorName: string }>();
    if (!allDetails) return map;
    for (const d of allDetails.candidates) {
      const teamleadId = teamleadIdByTeam.get(d.teamNumber);
      const info = extractPositionInfo(d.formSubmissions, teamleadId);
      if (info) map.set(d.id, info);
    }
    return map;
  }, [allDetails, teamleadIdByTeam]);

  const tierMap = useMemo(() => {
    const map = new Map<string, Tier>();
    for (const c of candidates) {
      const p = positionMap.get(c.id);
      if (p) map.set(c.id, p.section ?? positionToTier(p.position, p.teamSize));
    }
    return map;
  }, [candidates, positionMap]);

  const teamNumbers = useMemo(
    () => Array.from(new Set(candidates.map(c => c.teamNumber))).sort((a, b) => a - b),
    [candidates]
  );

  // Unlike team, matam isn't guaranteed to be set on every candidate — skip
  // null/undefined so the filter options only list matams actually in use.
  const matamNumbers = useMemo(
    () => Array.from(new Set(
      candidates.map(c => c.candidatePersonalMatam).filter((m): m is number => m != null)
    )).sort((a, b) => a - b),
    [candidates]
  );

  const evaluatorOptions = useMemo(() => {
    if (!allDetails) return [];
    const names = new Set<string>();
    for (const d of allDetails.candidates)
      for (const sub of d.formSubmissions)
        names.add(`${sub.submittedBy.firstName} ${sub.submittedBy.lastName}`.trim());
    return Array.from(names).sort();
  }, [allDetails]);

  const redFlagSet = useMemo(() => {
    if (!allDetails) return new Set<string>();
    return computeRedFlagSet(allDetails.candidates, new Map(formDefs.map(f => [f.id, f])));
  }, [allDetails, formDefs]);

  // Enrich rows first, then filter on the *enriched* fields (display name,
  // tier) — filtering the raw candidate first and computing display data
  // afterwards (the previous order) meant search only ever matched
  // `detail.fullName`, never the CSV-enriched/firstName+lastName-composed
  // name actually shown on the card, so a name a user could see and type
  // often wouldn't match anything.
  const filteredRows: TableRow[] = useMemo(() => {
    const enriched: TableRow[] = candidates.map(c => {
      const detail  = nameMap.get(c.id);
      const posInfo = positionMap.get(c.id);
      const _candidateRankInTeam = allDetails
        ? (posInfo ? `${posInfo.position}/${posInfo.teamSize}` : 'unknown')
        : null;
      const csvRow = csvEnrichment.get(c.candidateNumber);
      // CSV enrichment outranks the API here too, same reasoning as the detail
      // drawer — an imported roster is actively correcting/supplementing what
      // the backend knows, so it should win in this session's display.
      const csvName = findEnrichedValue(csvRow, ['name', 'candidateName', 'fullName', 'candidateFullName']);
      return {
        ...c,
        _tier: tierMap.get(c.id) ?? null,
        _rank: positionMap.get(c.id)?.position ?? null,
        _displayName:
          csvName
          ?? detail?.fullName
          ?? (detail?.firstName ? `${detail.firstName} ${detail.lastName ?? ''}`.trim() : null)
          ?? c.fullName
          ?? (c.firstName ? `${c.firstName} ${c.lastName ?? ''}`.trim() : null)
          ?? null,
        _namesLoading: namesLoading,
        _candidateRankInTeam,
        _redFlag: redFlagSet.has(c.id),
      };
    });

    const q = search.trim().toLowerCase();
    let result = enriched;
    if (q) result = result.filter(row =>
      (row._displayName?.toLowerCase().includes(q) ?? false) || String(row.candidateNumber).includes(q)
    );
    if (statusFilter !== 'all') result = result.filter(row => row.status === statusFilter);
    if (teamFilter   !== 'all') result = result.filter(row => row.teamNumber === Number(teamFilter));
    if (matamFilter === 'unknown') result = result.filter(row => row.candidatePersonalMatam == null);
    else if (matamFilter !== 'all') result = result.filter(row => row.candidatePersonalMatam === Number(matamFilter));
    if (tierFilter   !== 'all') result = result.filter(row => row._tier === tierFilter);
    if (evalFilter   !== 'all') result = result.filter(row =>
      nameMap.get(row.id)?.formSubmissions.some(s => `${s.submittedBy.firstName} ${s.submittedBy.lastName}`.trim() === evalFilter)
    );

    return result;
  }, [candidates, search, statusFilter, teamFilter, matamFilter, tierFilter, evalFilter, tierMap, nameMap, namesLoading, positionMap, allDetails, redFlagSet, csvEnrichment]);

  // Auto-select active cycle, or most recently updated cycle if none is active
  useEffect(() => {
    if (!cycleId && cycles?.length) {
      const act = cycles.find(c => c.status === CycleStatus.ACTIVE);
      if (act) {
        setCycleId(act.id);
      } else {
        const recent = [...cycles].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
        if (recent) setCycleId(recent.id);
      }
    }
  }, [cycles, cycleId]);

  // ── Handlers ──
  const handleCsvImport = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const { headers, rows } = parseSimpleCSV(text);
      const numCol = headers.find(h => {
        const n = h.toLowerCase().replace(/[\s_-]/g, '');
        return ['candidatenumber', 'candidateno', 'number', 'num', '#'].includes(n);
      });
      if (!numCol) { setToast({ text: tr('sum.csvNeedsNumber', lang), sev: 'error' }); return; }
      const map = new Map<number, Record<string, string>>();
      const extraCols = headers.filter(h => h !== numCol);
      for (const row of rows) {
        const n = parseInt(row[numCol], 10);
        if (!isNaN(n)) {
          const data: Record<string, string> = {};
          for (const col of extraCols) if (row[col]?.trim()) data[col] = row[col].trim();
          if (Object.keys(data).length) map.set(n, data);
        }
      }
      setCsvEnrichment(map);
      setHasUnsavedCsv(true);
      setToast({ text: tr('sum.merged', lang).replace('{n}', String(map.size)).replace('{cols}', String(extraCols.length)), sev: 'success' });
    };
    reader.readAsText(file);
  }, [lang]);

  const handleImagesImport = useCallback((files: File[]) => {
    const { map, skipped } = buildCandidateImageMap(files);
    setImageMap(map);
    setToast({
      text: tr('sum.imagesLoaded', lang).replace('{n}', String(map.size)) + (skipped ? ' ' + tr('sum.imagesSkipped', lang).replace('{n}', String(skipped)) : ''),
      sev: map.size ? 'success' : 'warning',
    });
  }, [lang]);

  const handleExportCSV = async () => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const [assignments, batch] = await Promise.all([
        formAssignments.length > 0 ? Promise.resolve(formAssignments) : apiService.getFormAssignmentsByCycle(cycleId),
        allDetails ?? apiService.getCandidateDetailsBatch(candidates.map(c => c.id)),
      ]);
      const defs: FormDefinition[] = assignments.map(a => ({
        id: a.id,
        name: a.template.name,
        formType: a.formType as FormDefinition['formType'],
        roleRelevancy: a.template.roleRelevancy ?? [],
        isActive: a.isActive,
        structure: a.structureSnapshot,
        weightInFinalScore: Number(a.weightInFinalScore),
        isMandatoryForClosure: a.isMandatoryForClosure,
        isTemplate: false,
        cycle: null,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
      }));
      const rankingMap = new Map(batch.candidates.map(c => {
        const p = positionMap.get(c.id);
        const t = tierMap.get(c.id);
        return [c.id, {
          rank:     p ? `${p.position}/${p.teamSize}` : '',
          tier:     t ?? '',
          rankedBy: p?.evaluatorName ?? '',
        }];
      }));
      const result = buildExportCSV(batch.candidates, defs, csvEnrichment.size > 0 ? csvEnrichment : undefined, rankingMap);
      const cycle  = sortedCycles.find(c => c.id === cycleId);
      downloadCSV(result.csv, `export_${cycle?.displayName ?? cycleId}_${new Date().toISOString().slice(0, 10)}.csv`);
      setToast({ text: tr('sum.exportedCsv', lang).replace('{n}', String(result.rowCount)).replace('{cols}', String(result.columnCount)), sev: 'success' });
    } catch (err: any) {
      setToast({ text: `${tr('sum.exportFailed', lang)}: ${err?.message ?? tr('sum.unknownError', lang)}`, sev: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const batch = allDetails ?? await apiService.getCandidateDetailsBatch(candidates.map(c => c.id));
      const cycle = sortedCycles.find(c => c.id === cycleId);
      const enrichedCandidates = batch.candidates.map(c => {
        const p = positionMap.get(c.id);
        const t = tierMap.get(c.id);
        const ranking = {
          _rank:     p ? `${p.position}/${p.teamSize}` : null,
          _tier:     t ?? null,
          _rankedBy: p?.evaluatorName ?? null,
        };
        const extra = csvEnrichment.get(c.candidateNumber);
        const csvMatam = extra?.['matam'] ?? extra?.['candidateMatam'] ?? null;
        const effectiveMatam = c.candidatePersonalMatam ?? (csvMatam != null ? csvMatam : null);
        return {
          ...c,
          ...ranking,
          ...(effectiveMatam != null ? { _effectiveMatam: effectiveMatam } : {}),
          ...(extra && Object.keys(extra).length > 0 ? { _enriched: extra } : {}),
        };
      });
      const payload = { cycle: { id: cycleId, displayName: cycle?.displayName, exportedAt: new Date().toISOString() }, candidates: enrichedCandidates };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${cycle?.displayName ?? cycleId}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setToast({ text: tr('sum.exportedJson', lang).replace('{n}', String(batch.candidates.length)), sev: 'success' });
    } catch (err: any) {
      setToast({ text: `${tr('sum.exportJsonFailed', lang)}: ${err?.message ?? tr('sum.unknownError', lang)}`, sev: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // ── Aggregate stats (used by fullscreen header) ──
  const fsStats = useMemo(() => {
    const scored = filteredRows.filter(r => r.finalCycleScore != null);
    const avg    = scored.length ? scored.reduce((s, r) => s + Number(r.finalCycleScore), 0) / scored.length : null;
    const tierCounts = { top: 0, mid: 0, bottom: 0 };
    for (const r of filteredRows) { if (r._tier && r._tier in tierCounts) tierCounts[r._tier as keyof typeof tierCounts]++; }
    return { avg, tierCounts };
  }, [filteredRows]);

  const currentCycleName = cycleOptions.find(o => o.value === cycleId)?.label ?? cycleId;

  // ── Shared panel props (used in both normal and fullscreen layouts) ──
  const sharedListProps = {
    rows: filteredRows,
    totalCount: candidates.length,
    selectedId: selectedCandidate?.id ?? null,
    onSelect: (row: TableRow) => setSelectedCandidate(row),
    search, onSearch: setSearch,
    statusFilter, onStatusFilter: setStatusFilter,
    teamFilter, onTeamFilter: setTeamFilter,
    matamFilter, onMatamFilter: setMatamFilter,
    tierFilter, onTierFilter: setTierFilter,
    evaluatorFilter: evalFilter, onEvaluatorFilter: setEvalFilter,
    teamNumbers, matamNumbers, evaluatorOptions,
    cycleId, cycleOptions,
    onCycleChange: (id: string) => { setCycleId(id); setSelectedCandidate(null); setImageMap(new Map()); },
    searchInputRef: searchRef,
  };

  const cycleIsClosed = useMemo(
    () => (cycles ?? []).find(c => c.id === cycleId)?.status === CycleStatus.CLOSED,
    [cycles, cycleId]
  );

  const sharedDetailProps = {
    candidate: selectedCandidate,
    detail: selectedCandidate ? nameMap.get(selectedCandidate.id) ?? null : null,
    detailLoading: namesLoading,
    formDefs,
    tier: selectedCandidate ? tierMap.get(selectedCandidate.id) ?? null : null,
    candidateRankInTeam: (() => {
      if (!selectedCandidate || !allDetails) return null;
      const p = positionMap.get(selectedCandidate.id);
      return p ? `${p.position}/${p.teamSize}` : 'unknown';
    })(),
    rankEvaluatorName: selectedCandidate ? (positionMap.get(selectedCandidate.id)?.evaluatorName ?? null) : null,
    csvEnrichment: selectedCandidate ? csvEnrichment.get(selectedCandidate.candidateNumber) : undefined,
    candidateImage: selectedCandidate ? imageMap.get(selectedCandidate.candidateNumber) : undefined,
    hideSocioEvals: cycleIsClosed,
  };

  // ── Render ──
  const canExport = !!cycleId && candidates.length > 0 && !cycleLoading;

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>

      {/* ── Masthead ── */}
      <Box sx={{ flexShrink: 0, py: '10px', borderBottom: `1px solid ${T.border}` }}>
        <Typography variant="caption" sx={{ color: T.textMuted, fontWeight: 600 }}>{tr('page.breadcrumb', lang)}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: '4px', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h1" sx={{ color: T.ink, fontWeight: 700 }}>{tr('page.title', lang)}</Typography>
            <Typography variant="body2" sx={{ mt: '6px', color: T.textMuted }}>{tr('page.subtitle', lang)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', pb: '4px' }}>
            {exporting && <CircularProgress size={14} sx={{ color: T.textSubtle }} />}
            <ToolbarButton
              disabled={!canExport || exporting}
              onClick={e => setExportAnchor(e.currentTarget)}
            >
              {tr('btn.export', lang)}
            </ToolbarButton>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleCsvImport(f); e.target.value = ''; }}
            />
            <ToolbarButton onClick={() => csvInputRef.current?.click()}>
              {tr('btn.importCsv', lang)}
            </ToolbarButton>
            <input
              ref={imagesInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              {...({ webkitdirectory: '' } as Record<string, string>)}
              onChange={e => {
                const fs = e.target.files;
                handleImagesImport(fs ? Array.from(fs) : []);
                e.target.value = '';
              }}
            />
            <ToolbarButton onClick={() => imagesInputRef.current?.click()}>
              {tr('btn.importImages', lang)}
            </ToolbarButton>
            <ToolbarButton onClick={() => setIsFullscreen(true)}>
              ⛶ {tr('btn.fullscreen', lang)}
            </ToolbarButton>
          </Box>
        </Box>
      </Box>

      {/* ── Unsaved CSV banner ── */}
      {hasUnsavedCsv && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0,
          px: '16px',
          py: '8px',
          bgcolor: '#FFFBEB',
          border: `1px solid #FDE68A`,
          borderInlineStart: `3px solid #D97706`,
        }}>
          <Typography sx={{ fontSize: 12, color: '#92400E', flex: 1 }}>
            <strong>{tr('banner.unsaved', lang)}</strong> — {tr('banner.unsavedBody', lang)}
          </Typography>
          <Box
            component="button"
            onClick={() => { setCsvEnrichment(new Map()); setHasUnsavedCsv(false); }}
            sx={{ fontSize: 11, color: '#92400E', cursor: 'pointer', border: 'none', bgcolor: 'transparent', textDecoration: 'underline', p: 0 }}
          >
            {tr('btn.clear', lang)}
          </Box>
        </Box>
      )}

      {/* ── Two-panel content ── */}
      {cycleError ? (
        <ErrorAlert error={cycleError} title={tr('sum.errorLoad', lang)} />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', border: `1px solid ${T.border}`, overflow: 'hidden' }}>

          {/* Left panel — sidebar */}
          <Box sx={{
            width: sidebarCollapsed ? 48 : 340,
            flexShrink: 0,
            borderInlineEnd: `1px solid ${T.border}`,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            transition: 'width 200ms ease',
          }}>
            {cycleLoading ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <CircularProgress size={14} sx={{ color: T.textSubtle }} />
                <Typography sx={{ fontSize: 12, color: T.textMuted }}>{tr('loading', lang)}</Typography>
              </Box>
            ) : (
              <CandidateListPanel
                {...sharedListProps}
                isCollapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(v => !v)}
              />
            )}
          </Box>

          {/* Right panel — detail */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <CandidateDetailDrawer {...sharedDetailProps} />
          </Box>

        </Box>
      )}

      {/* ── Fullscreen overlay ── */}
      {isFullscreen && (
        <Box sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          bgcolor: T.bg,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Top bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            px: '20px',
            py: '10px',
            borderBottom: `1px solid ${T.border}`,
            bgcolor: T.surface,
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{currentCycleName}</Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: T.borderSoft }} />
            <Typography sx={{ fontSize: 12, color: T.textSubtle }}>
              {filteredRows.length} {tr('list.candidates', lang)}
            </Typography>
            {fsStats.avg != null && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: T.borderSoft }} />
                <Typography sx={{ fontSize: 12, color: T.textSubtle }}>
                  Avg {fsStats.avg.toFixed(1)}
                </Typography>
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ borderColor: T.borderSoft }} />
            <Box sx={{ display: 'flex', gap: '10px' }}>
              {[
                { label: tr('tier.top', lang), count: fsStats.tierCounts.top, color: T.tierTop },
                { label: tr('tier.mid', lang), count: fsStats.tierCounts.mid, color: T.tierMid },
                { label: tr('tier.bottom', lang), count: fsStats.tierCounts.bottom, color: T.tierBottom },
              ].map(({ label, count, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                  <Typography sx={{ fontSize: 12, color: T.textSubtle }}>{label} {count}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: 1 }} />

            {/* Zoom — detail-view-only, per fullscreen session; resets on exit */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <ToolbarButton
                onClick={() => setTextZoom(z => Math.max(0.75, Math.round((z - 0.1) * 100) / 100))}
              >
                A−
              </ToolbarButton>
              <Typography sx={{ fontSize: 11, color: T.textSubtle, minWidth: 34, textAlign: 'center' }}>
                {Math.round(textZoom * 100)}%
              </Typography>
              <ToolbarButton
                onClick={() => setTextZoom(z => Math.min(1.75, Math.round((z + 0.1) * 100) / 100))}
              >
                A+
              </ToolbarButton>
              {textZoom !== 1 && (
                <ToolbarButton onClick={() => setTextZoom(1)}>
                  {tr('btn.resetZoom', lang)}
                </ToolbarButton>
              )}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: T.borderSoft }} />
            <ToolbarButton onClick={exitFullscreen}>
              ⛶ {tr('btn.exitFullscreen', lang)}
            </ToolbarButton>
          </Box>

          {/* Two-panel content */}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* List panel — wider in fullscreen */}
            <Box sx={{
              width: sidebarCollapsed ? 48 : 400,
              flexShrink: 0,
              borderInlineEnd: `1px solid ${T.border}`,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              transition: 'width 200ms ease',
            }}>
              <CandidateListPanel
                {...sharedListProps}
                isFullscreen
                isCollapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(v => !v)}
              />
            </Box>
            {/* Detail panel — fills remaining space; zoom applied here only,
                so the candidate list stays at a stable, scannable size.
                CSS `zoom` (not `transform: scale`) reflows layout instead of
                just scaling pixels, so the grid actually re-wraps at each
                level — but it's non-standard: Chromium/Safari support it
                natively, Firefox only since v126. Acceptable here since this
                is an internal tool on managed/Chromium-based browsers; if
                that ever changes, fall back to a `transform: scale` + explicit
                width compensation instead. */}
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }} style={{ zoom: textZoom }}>
              <CandidateDetailDrawer {...sharedDetailProps} isFullscreen />
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Export menu ── */}
      <Menu
        anchorEl={exportAnchor}
        open={!!exportAnchor}
        onClose={() => setExportAnchor(null)}
        slotProps={{ paper: { sx: { border: `1px solid ${T.border}`, boxShadow: 'none', borderRadius: 0, minWidth: 140 } } }}
      >
        <MenuItem onClick={handleExportCSV} sx={{ fontSize: 13 }}>{tr('sum.exportCsv', lang)}</MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleExportJSON} sx={{ fontSize: 13 }}>{tr('sum.exportJson', lang)}</MenuItem>
      </Menu>

      {/* ── Toast ── */}
      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} severity={toast?.sev ?? 'success'} variant="filled" sx={{ width: '100%', borderRadius: 0 }}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SummaryPage;
